<template>
  <div class="payroll-table">
    <div class="payroll-header-container" style="margin: 10px auto;">
      <div class="dropdowns-container">
        <div class="admin-access-section">
          <EmployeeDropdown
            v-if="!isEmployeeSearchHidden"
            :employees="employeeList"
            :company="$store.state.auth.company"
            defaultFilter="active"
            :disabled="isSelectLoading || !payrollData.length"
            :loading="isSelectLoading"
            @select="selectEmployee"
            @update="filteredUsers = $event"
          />
        </div>
        <div class="search-group">
          <div class="searchable-1">
            <SearchableDropdown
              :items="monthItems"
              labelKey="name"
              :initialValue="{ name: selectedMonth }"
              placeholder="Select Month"
              @select="selectMonth"
            ></SearchableDropdown>
          </div>
          <div class="searchable-2">
            <SearchableDropdown
              :items="yearItems"
              labelKey="name"
              :initialValue="{ name: selectedYear }"
              @select="selectYear"
              placeholder="Select Year"
            />
          </div>
        </div>

        <button v-if="this.isAdmin" class="btn btn-outline export-icon-button" @click="exportToExcel">
          <span class="export-icon">↓</span>
          Export
        </button>
        <TaxReportExport v-if="isAdmin" />
      </div>
      <div class="filter-container mb-3">
        <div class="custom-btn-toggle">
          <button 
            @click="switchTab('payroll')" 
            :class="{ 'active-tab': activeTab === 'payroll' }" 
            class="tab-button"
            style="width: 120px; display: flex; justify-content: center;"
          >
            Payroll
          </button>
          <button 
            @click="switchTab('adjustments')" 
            :class="{ 'active-tab': activeTab === 'adjustments' }" 
            class="tab-button"
            style="width: 120px; display: flex; justify-content: center;"
          >
            {{ isAdmin ? 'Adjustments' : 'My Expenses' }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="activeTab === 'payroll'">
      <div v-if="isAdmin" style="margin: 10px 0px;">
        <CardsSection :cards="cards" />
      </div>

      <div v-if="isAdmin" class="table-container" style="position: relative;">
        <CustomTable :loading="loading ? loading : tableFilterLoading" :columns="columns" :data="filteredPayrollData"
          :itemsPerPageOptions="[5, 10, 20, 50]">
          <template v-slot:employeeName="{ item }">
            {{ item.employeeName }}
          </template>
          <template v-slot:grossSalary="{ item }">
            {{ item.grossSalary }}
          </template>
          <template v-slot:taxDeduction="{ item }">
            {{ item.taxDeduction ? item.taxDeduction : '0' }}
          </template>
          <template v-slot:status="{ item }">
            <span :class="getStatusClass(item.status)">
              {{ item.status }}
            </span>
          </template>
          <template v-slot:obtainedLeaves="{ item }">
            <span class="text-ellipsis" :title="getLeavesTooltip(item)">
              {{ getLeavesTaken(item) }}
            </span>
          </template>
          <template v-slot:daysWorked="{ item }">
            <span class="text-ellipsis" :title="`Required Working days: ${item.requiredWorkingDays}`">
              {{ item.daysWorked }}
            </span>
          </template>
          <template v-slot:lateDeduction="{ item }">
            <span class="text-ellipsis" :title="getLateDeductionTooltip(item)">
              {{ formatLateDeduction(item) }}
            </span>
          </template>
          <template v-slot:action="{ item }">
            <div @click="viewDetails(item)" style="display: flex; justify-content: center;">
              <div class="payroll-action-button">
                <PencilIcon color="gray" :width="18" :height="18"/>
              </div>
            </div>
          </template>
        </CustomTable>

        <div v-if="this.isLoading" class="payroll-table-loader-overlay">
          <div class="payroll-loader-container">
            <DoubleLoader class="payroll-loader" />
          </div>
        </div>
      </div>

      <div v-if="noPayrollFound" class="no-payroll-card">
        <p class="no-payroll-title">No Payroll Data</p>
        <p class="no-payroll-subtext">We couldn't find any payroll records for the selected period.</p>
      </div>
    </div>

    <div v-else-if="activeTab === 'adjustments'">
      <Adjustments 
        :selectedMonth="selectedMonth" 
        :selectedYear="selectedYear" 
        :employeeList="employeeList" 
        :filteredUsers="filteredUsers" 
        :isAdmin="isAdmin"
        :selectedEmployee="selectedEmployee"
      />
    </div>
  </div>
</template>

<script>
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import SearchableDropdown from "src/components/UIComponents/Dropdowns/SearchableDropdown.vue";
import EmployeeDropdown from "src/components/UIComponents/EmployeeDropdown.vue";
import CardsSection from "../../LeaveManagement/CardsSection.vue";
import { MainTableColumns } from "./tableColumns";
import { generateYearsArray, stripeLeadingZero } from "../../../assets/config/helpers";
import { leaveEncashmentDecRule, leavesCarryForwardDecRule, monthNames } from "../../../assets/config/constants";
import firebaseDB from "../../../assets/config/firebase";
import firebase from "firebase/app";
import "firebase/database";
import DoubleLoader from "src/components/UIComponents/loaders/DoubleLoader.vue";
import PencilIcon from 'src/assets/icons/PencilIcon.js';
import Adjustments from "../Adjustments";
import TaxReportExport from "./TaxReportExport.vue";

export default {
  components: {
    CustomTable,
    SearchableDropdown,
    CardsSection,
    EmployeeDropdown,
    DoubleLoader,
    PencilIcon,
    Adjustments,
    TaxReportExport
  },
  data() {
    return {
      activeTab: this.tabFromQuery(this.$route.query.tab) || 'payroll',
      isAdmin: this.$store.state.auth.user.role === "admin",
      columns: MainTableColumns,
      selectedEmployee: null,
      cards: [],
      employeeList: this.getCompanyEmployees(),
      filteredUsers: [], // Store filtered employees from EmployeeDropdown
      months: monthNames,
      years: generateYearsArray(),
      loading: false,
      isSelectLoading: false,
      constants : this.$store.state.auth.constants || {},
      taxSlabs: {},
      holidaysList: [],
      tableFilterLoading: false,
    };
  },
  computed: {
    selectedMonth() {
      const currentDate = new Date();
      const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
      return this.$store.state.payroll.selectedMonth || currentMonth;
    },
    selectedYear() {
      return this.$store.state.payroll.selectedYear || new Date().getFullYear();
    },
    payrollData() {
      return this.$store.state.payroll.payrollData || [];
    },
    filteredPayrollData() {
      const {
        selectedEmployee,
        selectedMonth,
        selectedYear,
        filteredUsers
      } = this;

      this.tableFilterLoading = true;
      try {
        const filteredPayroll = this.payrollData.filter(payroll => {
          if (payroll.isSuperAdmin) return false
          if (payroll.joiningDate) {
            const joiningDate = new Date(payroll.joiningDate);
            const joiningYear = joiningDate.getFullYear();
            const joiningMonth = joiningDate.getMonth(); // 0–11
            // Convert selected month (name) to month index
            const selectedDate = new Date(`${selectedMonth} 1, ${selectedYear}`);
            const selectedYearNum = selectedDate.getFullYear();
            const selectedMonthNum = selectedDate.getMonth();
            // Check if selected month/year >= joining month/year
            if (
              selectedYearNum < joiningYear ||
              (selectedYearNum === joiningYear && selectedMonthNum < joiningMonth)
            ) {
              return false;
            }
          }
          const employeeMatch = !selectedEmployee || payroll.employeeId === selectedEmployee.uid;
          const monthMatch = !selectedMonth || payroll.month === selectedMonth;
          const yearMatch = !selectedYear || Number(payroll.year) === Number(selectedYear);
          // Filter by UIDs in filteredUsers (from EmployeeDropdown)
          const userMatch = filteredUsers.length === 0 || filteredUsers.some(user => user.uid === payroll.employeeId);
          return employeeMatch && monthMatch && yearMatch && userMatch;
        })
          .map(payroll => {
            if (payroll.status === "Pending") {
              const updatedLeavesData = this.updateLeavesData(payroll, payroll.month);
              const updatedTaxDeduction = this.taxDeduction(payroll)
              const newAbsents = Math.max(0, payroll.requiredWorkingDays - payroll.daysWorked - payroll.obtainedLeaves.split(" / ")[0]);
              const finalAmount = this.finalAmountUpdate(payroll, updatedTaxDeduction,updatedLeavesData);
              const lateDeduction = this.calculateLateDeduction({ ...payroll, leavesQuota: updatedLeavesData.leavesQuota }).lateDeduction.toString();
              return {
                ...payroll,
                ...updatedLeavesData,
                absents: newAbsents,
                taxDeduction: updatedTaxDeduction,
                finalAmount,
                lateDeduction
              };
            } else {
              const updatedLeavesData = this.updateLeavesData(payroll, payroll.month);
              const lateDeduction = this.calculateLateDeduction({ ...payroll, leavesQuota: updatedLeavesData.leavesQuota }).lateDeduction.toString();
              return {
                ...payroll,
                lateDeduction
              };
            }
          });
        return filteredPayroll;
      } catch (error) {
        console.error("Error filtering payroll data:", error);
        return [];
      } finally {
        this.tableFilterLoading = false;
      }
    },
    isSearchDisabled() {
      return !this.selectedEmployee || !this.selectedMonth || !this.selectedYear;
    },
    isEmployeeSearchHidden() {
      return !this.isAdmin;
    },
    monthItems() {
      const months = this.months || monthNames;
      return months.map(month => ({ name: month }));
    },
    yearItems() {
      const years = this.years || generateYearsArray();
      return years.map(year => ({ name: year }));
    },
    isLoading() {
      return this.$store.state.payroll.loading;
    },
    noPayrollFound() {
      const hasPayrollId = this.employee && this.employee.payrollId && true;
      const isNotAdmin = !this.isAdmin;

      return !hasPayrollId && isNotAdmin;
    },
  },
  watch: {
    // e.g. clicking an expense notification while already on this page
    '$route.query.tab'(tab) {
      const target = this.tabFromQuery(tab);
      if (target) this.switchTab(target);
    },
    selectedMonth() {
      this.fetchPayrollData();
    },
    selectedYear() {
      this.fetchPayrollData();
    },
    selectedEmployee(newValue) {
      if (newValue) {
        this.fetchPayrollData();
      }
    },
    filteredUsers(newValue) {
      // Reset selectedEmployee and refresh table when filteredUsers changes
      if (newValue.length > 0 && this.selectedEmployee && !newValue.some(user => user.uid === this.selectedEmployee.uid)) {
        this.selectedEmployee = null;
        this.fetchPayrollData(); // Refresh table to reflect new filteredUsers
      } else if (!this.selectedEmployee) {
        this.fetchPayrollData(); // Refresh table when no employee is selected
      }
    },
    payrollData: {
      handler() {
        this.updateCards()
      },
      deep: true
    }
  },
  async created() {
    const company = this.$store.state.auth.company;
    try {
      if (!firebase.apps.length) {
        await firebaseDB.configureFirebase(company.firebase);
      }
    } catch (e) {
      console.error("Firebase initialization error:", e.message);
    }
    await this.fetchPayrollData();
  },
  beforeDestroy() {
    this.cleanupFirebaseListeners();
  },
  methods: {
    calculateLateDeduction(payrollData) {
      const lateArrivals = payrollData.lateArrivals || 0;
      const remainingLeaves = Math.floor(payrollData.leavesQuota.remainingLeaves) || 0;

      let lateDeduction = Math.floor(lateArrivals / 5);
      
      const adjustableFromLeaves = Math.min(2, remainingLeaves, lateDeduction);

      lateDeduction -= adjustableFromLeaves;

      if (payrollData.status !== "Pending") {
        lateDeduction = payrollData.lateDeductionDays ? Number(payrollData.lateDeductionDays) : 0
      } 

      return {
        lateDeduction,
        adjustedFromLeaves: adjustableFromLeaves
      };
    },
    finalAmountUpdate(selectedEmployee, updatedTaxDeduction, updatedLeavesData) {
      const leavesEncashmentDays = Number(Math.round(updatedLeavesData.leavesQuota.leavesEncashmentDays) || 0);

      let finalAmount = selectedEmployee.bonus - (Number(updatedTaxDeduction) || 0);

      const leavesEncashment = (selectedEmployee.perDaySalary * (Number(selectedEmployee.obtainedLeaves.split(" / ")[0]) + leavesEncashmentDays)).toFixed(2);

      const daysWorkedAmount = (selectedEmployee.perDaySalary * selectedEmployee.daysWorked).toFixed(2);

      finalAmount = finalAmount + Number(daysWorkedAmount) + Number(leavesEncashment)

      return Math.round(finalAmount);
    },
    formatPublicHolidays(selectedEmployee) {
      const holidays = this.holidaysList || [];
      const shift = (selectedEmployee.shift || "Morning").toLowerCase();
      const year = Number(selectedEmployee.year);
      const monthName = (selectedEmployee.month || "").toLowerCase();
      let workingDays = [];
      if (
        selectedEmployee &&
        Array.isArray(selectedEmployee.workingDays) &&
        selectedEmployee.workingDays.length
      ) {
        workingDays = selectedEmployee.workingDays.map(d => d.toLowerCase());
      } else {
        workingDays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
      }
      const formattedList = [];
      holidays.forEach(h => {
        const baseDate = new Date(h.dates);
        const isRecurring = !!h.isRecurring;
        const count = h.numberOfDays || 1;
        const hShift = (h.shift || "general").toLowerCase();
        if (
          !(shift === "general" ||
            hShift === "general" ||
            hShift === shift)
        ) {
          return;
        }
        const startDate = new Date(
          isRecurring ? year : baseDate.getFullYear(),
          baseDate.getMonth(),
          baseDate.getDate()
        );
        for (let i = 0; i < count; i++) {
          const d = new Date(startDate);
          d.setDate(startDate.getDate() + i);
          const dayName = d
            .toLocaleDateString("en-US", { weekday: "long" })
            .toLowerCase();
          if (!workingDays.includes(dayName)) continue;
          const dMonthName = d
            .toLocaleString("default", { month: "long" })
            .toLowerCase();
          if (monthName && dMonthName !== monthName) continue;
          const dd = ("0" + d.getDate()).slice(-2);
          const mm = ("0" + (d.getMonth() + 1)).slice(-2);
          const yyyy = d.getFullYear();
          formattedList.push({
            text: `${dd}-${mm}-${yyyy}: ${h.name}`,
            dateObj: d
          });
        }
      });
      formattedList.sort((a, b) => a.dateObj - b.dateObj);
      return formattedList.map(i => i.text);
    },
    async fetchHolidayData() {
      return await firebaseDB.getAllHolidays();
    },
    taxDeduction(employee) {
      const availableYears = Object.keys(this.taxSlabs).map(Number).sort((a, b) => b - a);
      const selectedYear = availableYears.includes(Number(employee.year)) ? Number(employee.year) : availableYears[0] || 2025;
      const slabs = this.taxSlabs[selectedYear] || {};
      const annualSalary = ((employee.basicSalary || 0) + (employee.extraAllowance || 0)) * 12;
      const medicalExemption = annualSalary * 0.065;
      const taxableSalary = annualSalary - medicalExemption;
      let annualTax = 0;
      const thresholds = Object.keys(slabs).map(Number).sort((a, b) => a - b);
      for (let i = 0; i < thresholds.length; i++) {
        const threshold = thresholds[i];
        const slab = slabs[threshold];
        const nextThreshold = thresholds[i + 1] || Infinity;
        if (taxableSalary >= threshold && taxableSalary < nextThreshold) {
          annualTax = slab.fixed + (taxableSalary - threshold) * (slab.percentage / 100);
          break;
        }
      }
      const monthlyTax = annualTax / 12;
      this.taxDeductionInput = monthlyTax.toFixed(1);
      return stripeLeadingZero(monthlyTax.toFixed(1));
    },
    calculateJuneLeaveQuota(selectedEmployee) {
      const emp = selectedEmployee
      const leavesAllowed = Number(emp.leavesAllowed || 0)
      const leavesTaken = Number(emp.DBLeavesTaken || 0)
      const leavesAvailable = Number(emp.leavesAvailable || 0)
      const remainingLeaves = leavesAllowed - leavesTaken
      const newAllowedLeaves = leavesAvailable - leavesAllowed + remainingLeaves
      const updatedLeaves = newAllowedLeaves
      return updatedLeaves
    },
    calculateJuneCarryForward(selectedEmployee, selectedMonth) {
      if (selectedMonth !== "June") return 0;
      const emp = selectedEmployee
      const remaininingAllowedLeaves = Number(emp.leavesAllowed || 0) - Number(emp.DBLeavesTaken || 0)
      return remaininingAllowedLeaves
    },
    calculateEncashment(remainingLeaves, selectedMonth) {
      if (selectedMonth !== "December") return 0;
      const remaining = Math.min(Math.floor(remainingLeaves), 5);
      const encashmentValue = leaveEncashmentDecRule[remaining] || 0;
      let leavesEncashmentDays = encashmentValue;
      let annualLeavesEncashmentDays = encashmentValue;
      return { encashmentValue, leavesEncashmentDays, annualLeavesEncashmentDays };
    },
    calculateDecCarryForward(remainingLeaves, selectedMonth) {
      if (selectedMonth !== "December") return 0;
      const remaining = Math.min(Math.floor(remainingLeaves), 5);
      return leavesCarryForwardDecRule[remaining] || 0;
    },
    calculateUpdatedLeaveQuota(selectedEmployee, updatedLeaveQuota, carryForwardDays, remainingLeaves, selectedMonth) {
      if (selectedMonth === "December") {
        return Number(this.constants.leavesAvailable || 0) + carryForwardDays;
      }
      if (selectedMonth === "June") {
        return Number(remainingLeaves) + (Number(selectedEmployee.leavesAvailable) / 2);
      }
      return updatedLeaveQuota;
    },
    updateLeavesData(selectedEmployee, selectedMonth) {
      if (!selectedEmployee) return {};
      const leavesQuota = selectedEmployee.leavesQuota || {};
      let remainingLeaves = Number(selectedEmployee.leavesAllowed) - Number(selectedEmployee.DBLeavesTaken) - Number(selectedEmployee.leavesDeductionDays) || 0;
      remainingLeaves = Math.max(0, remainingLeaves);
      let perDaySalary = selectedEmployee.perDaySalary || 0;
      let carryForwardDays = leavesQuota.carryForwardDays || 0;
      let updatedLeaveQuota = leavesQuota.updatedLeaveQuota || 0;
      let leavesEncashmentDays = leavesQuota.leavesEncashmentDays || 0;
      let annualLeavesEncashmentDays = leavesQuota.annualLeavesEncashmentDays || 0;
      if (selectedMonth === "June") {
        updatedLeaveQuota = Number(this.calculateJuneLeaveQuota(selectedEmployee) || leavesQuota.updatedLeaveQuota) || 0;
        carryForwardDays = this.calculateJuneCarryForward(selectedEmployee, selectedMonth) || leavesQuota.carryForwardDays || 0;
      } else if (selectedMonth === "December") {
        let calculatedEncashment = this.calculateEncashment(remainingLeaves, selectedMonth).encashmentValue || leavesQuota.leavesEncashmentDays || 0;
        carryForwardDays = this.calculateDecCarryForward(remainingLeaves, selectedMonth) || leavesQuota.carryForwardDays || 0;
        calculatedEncashment = this.calculateEncashment(remainingLeaves, selectedMonth).encashmentValue;
        leavesEncashmentDays = calculatedEncashment;
        annualLeavesEncashmentDays = calculatedEncashment;
        updatedLeaveQuota = Number(this.calculateUpdatedLeaveQuota(selectedEmployee, updatedLeaveQuota, carryForwardDays, remainingLeaves, selectedMonth) || leavesQuota.updatedLeaveQuota) || 0;
      }
      return {
        perDaySalary,
        annualLeavesEncashmentDays,
        leavesQuota: { carryForwardDays, leavesEncashmentDays, remainingLeaves, updatedLeaveQuota }
      };
    },
    getCompanyEmployees() {
      const companyUserIds = this.$store.state.auth.company.users.map(user => user.uid);
      return this.$store.state.auth.allUsers.filter(user =>
        companyUserIds.includes(user.uid)
      );
    },

    updateCards() {
      let totalTax = 0
      let totalSalary = 0
      let processed = 0
      let totalEmp = 0

      this.filteredPayrollData.map(item => {
        totalSalary += +item.finalAmount || 0
        totalTax += +item.taxDeduction || 0
        processed += item.status === "Processed"
        totalEmp += 1
      });

      this.cards = [
        { value: totalEmp, label: "Total Employees", color: "#00aaff" },
        { value: processed, label: "Processed", color: "green" },
        { value: Math.round(totalSalary), label: "Total Salaries", color: "orange" },
        { value: Math.round(totalTax), label: "Total Taxes", color: "#00c7c7" },
      ];
    },

    async fetchPayrollData() {
      this.loading = true;
      try {
        // Non-admins are redirected to their processed payroll; don't pull them off the expenses tab
        const router = {
          push: (location) => {
            if (this.activeTab === 'payroll') this.$router.push(location);
          },
        };
        await this.$store.dispatch('fetchPayrollRealTime', { router });
      } catch (error) {
        console.error("Error fetching payroll data:", error);
      } finally {
        this.loading = false;
        this.updateCards();
      }
    },

    cleanupFirebaseListeners() {
      const selectedYear = this.selectedYear || new Date().getFullYear();
      const selectedMonth = this.selectedMonth || monthNames[new Date().getMonth()];
      const payrollRef = firebase.database().ref(`payroll/${selectedYear}/${selectedMonth}`);
      payrollRef.off();
    },

    tabFromQuery(tab) {
      return tab === 'expenses' || tab === 'adjustments' ? 'adjustments' : tab === 'payroll' ? 'payroll' : null;
    },

    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      // Re-run the non-admin redirect check that was skipped while on the expenses tab
      if (tab === 'payroll' && !this.isAdmin) this.fetchPayrollData();
    },

    async selectEmployee(employee) {
      this.selectedEmployee = employee;
      await this.fetchPayrollData();
    },

    selectMonth(month) {
      this.$store.commit("setSelectedMonth", month ? month.name : "");
    },

    selectYear(year) {
      this.$store.commit("setSelectedYear", year ? year.name : "");
    },

    viewDetails(item) {
      this.$store.commit("setSelectedEmployeePayroll", item);
      this.$router.push({
        name: "Payroll Details",
        params: {
          employeeId: item.employeeId,
          employee: item
        }
      });
    },

    // UI Helper Methods
    getStatusClass(status) {
      return {
        'payroll-table-status-processed': status === 'Processed',
        'payroll-table-status-draft': status === 'Draft',
        'payroll-table-status-pending': status !== 'Processed' && status !== 'Draft'
      };
    },

    getLeavesTaken(item) {
      const parts = (item.obtainedLeaves || '0 / 0').split(' / ');
      return parts[0] || 0;
    },

    getLateDeductionTooltip(item) {
      const parts = (item.lateDeduction || '0 - 0').split(' - ');
      return `Late Deduction Days: ${parts[1] || 0}`;
    },

    formatLateDeduction(item) {
      const parts = (item.lateDeduction || '0 - 0').split(' - ');
      return (parseFloat(parts[0]) || 0).toFixed(2);
    },

    getLeavesTooltip(item) {
      const parts = (item.obtainedLeaves || '0 / 0').split(' / ');
      return `Remaining Leaves: ${(Number(parts[1] || 0))}`;
    },

    exportToExcel() {
      if (!this.isAdmin || !this.filteredPayrollData.length) return;

      const payrollArray = this.filteredPayrollData.map(item => ({
        "Employee ID": item.employeeId || "N/A",
        "Employee Name": item.employeeName || "Unknown",
        "Basic Salary": item.basicSalary || 0,
        "Extra Allowance": item.extraAllowance || 0,
        "Total Working Days": item.workingDays || 22,
        "Days Worked": item.daysWorked || 0,
        "Leaves Taken": item.leavesTaken || 0,
        "Absents": item.absents || 0,
        "Late Arrivals": item.lateArrivals || 0,
        "Leave Balance": item.leaveBalance || 0,
        "Per Day Salary": item.perDaySalary || 0,
        "Late Deduction": item.lateDeduction || 0,
        "Extra Amount Adjustment": item.extraAmountAdjustment || 0,
        "Extra Amount Adjustment Note": item.extraAmountAdjustmentNote || "N/A",
        "Leave Deduction Note": item.extraAmountAdjustmentNote || "N/A",
        "Leave Deduction Days": item.leavesDeductionDays || "N/A",
        "Bonus": item.bonus || 0,
        "Transaction Fees": item.transactionFees || 0,
        "Leaves Encashment": item.leavesEncashment || 0,
        "Month": item.month || this.selectedMonth,
        "Year": item.year || this.selectedYear,
        "Status": item.status || "Pending",
        "Final Amount": item.finalAmount || 0,
      }));

      const worksheet = XLSX.utils.json_to_sheet(payrollArray);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll Data");

      const fileName = `Payroll_${this.selectedMonth}_${this.selectedYear}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    },
    async fetchTaxSlabs() {
      this.taxSlabs = await firebaseDB.fetchTaxSlabs();
    }
  },
  async mounted() {
    this.fetchTaxSlabs();
    this.holidaysList = await this.fetchHolidayData();
  }
};
</script>

<style scoped>
.payroll-header-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.filter-container {
  display: flex;
  justify-content: center;
  margin-top: -5px;
  margin-bottom: 5px;
}

.custom-btn-toggle {
  display: flex;
  border-radius: 8px;
  background-color: #ece8e8;
  padding: 3px 0px;
  align-items: center;
  height: 42px;
}

.custom-btn-toggle button {
  border: none;
  gap: 3px;
  border-radius: 8px;
  padding: 5px 12px;
  height: 35px;
  font-size: 14px;
  font-weight: 500;
  text-transform: none;
  transition: all 0.2s ease;
  margin: 0 4px;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.custom-btn-toggle button.active-tab {
  background-color: #ffffff !important;
  color: #333 !important;
  border: none;
  outline: none;
}

.custom-btn-toggle button:hover:not(.active-tab) {
  background-color: #e0e0e0;
}
</style>


// WEBPACK FOOTER //
// src/components/PayrollManagement/PayrollSummary/PayrollTable.vue